import React from 'react';
import { Card } from '../../common/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/I18nProvider';
import { useWarehouse } from '../../../contexts/WarehouseContext';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { usePublishedDate } from '../../../hooks/usePublishedDate';
import { useAppVersion } from '../../../hooks/useAppVersion';
import { OPERATOR_MODULES } from '../../../constants/operatorModules';

interface DashboardProps {
  onModuleChange: (moduleId: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onModuleChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currentWarehouse, warehouses } = useWarehouse();
  const { isModuleAccessible } = useModuleAccess();
  const publishedDate = usePublishedDate();
  const appVersion = useAppVersion();
  const isAdvancedWarehouse = warehouses.find(w => w.warehouseID === currentWarehouse)?.useAWYesNo ?? false;

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-900',
      green: 'bg-green-50 hover:bg-green-100 border-green-200 text-green-900',
      yellow: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 text-yellow-900',
      orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-900',
      indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-900',
      red: 'bg-red-50 hover:bg-red-100 border-red-200 text-red-900',
      cyan: 'bg-cyan-50 hover:bg-cyan-100 border-cyan-200 text-cyan-900',
      teal: 'bg-teal-50 hover:bg-teal-100 border-teal-200 text-teal-900',
      gray: 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900',
      emerald: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-900',
      slate: 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-900',
      blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-900'
    };
    return colorMap[color] || 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900';
  };

  const getIconColorClasses = (color: string) => {
    const iconColorMap: { [key: string]: string } = {
      purple: 'text-purple-600',
      green: 'text-green-600',
      yellow: 'text-yellow-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600',
      red: 'text-red-600',
      cyan: 'text-cyan-600',
      teal: 'text-teal-600',
      gray: 'text-gray-600',
      emerald: 'text-emerald-600',
      slate: 'text-slate-600',
      blue: 'text-blue-600'
    };
    return iconColorMap[color] || 'text-gray-600';
  };

  return (
    <div className="relative min-h-screen">
      {/* Main content area - med padding bottom for fast statusbar */}
      <div className="pb-16 sm:pb-20">
        <div className="p-3 sm:p-4 max-w-4xl mx-auto">
          {/* Header område for plukkerelevant informasjon */}
          <div className="mb-4 sm:mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 sm:p-4 rounded-lg shadow-lg flex items-center gap-4 sm:gap-6">
              <img src="/compass-logo-white.png" alt="Compass" className="h-10 sm:h-12 object-contain flex-shrink-0" />
              <div className="w-px h-10 sm:h-12 bg-white/40 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold mb-1 sm:mb-2 truncate">
                  {t('dashboard.welcome', { '0': user?.operatorName || '' })}
                </h2>
                <div className="text-xs sm:text-sm opacity-90">
                  <p className="truncate">
                    {t('dashboard.status')}: {t('dashboard.statusReady')} •
                    {t('dashboard.activePicks')}: 0 •
                    {t('dashboard.pendingTasks')}: 0
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Modulkort - 3 kolonner på mobil, responsivt */}
          {/* Filtrert basert på operatørens modultilgang (profilinnstillinger) */}
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
            {OPERATOR_MODULES
              .filter(m => isModuleAccessible(m.moduleId) && (!m.requiresAdvancedWarehouse || isAdvancedWarehouse))
              .map((module) => {
                const Icon = module.icon;
                const name = t(module.nameKey, {}, module.nameFallback);
                const shortName = t(module.shortNameKey, {}, module.shortNameFallback);
                const description = t(module.descriptionKey, {}, module.descriptionFallback);
                return (
                  <button
                    key={module.id}
                    onClick={() => onModuleChange(module.moduleId)}
                    className={`${getColorClasses(module.color)} p-2 sm:p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md text-left min-h-[90px] sm:min-h-[120px] flex flex-col active:scale-95 sm:hover:scale-105`}
                  >
                    <Icon
                      size={24}
                      className={`${getIconColorClasses(module.color)} mb-1 sm:mb-2 flex-shrink-0 sm:w-8 sm:h-8`}
                    />
                    <h3 className="font-semibold text-xs sm:text-sm leading-tight line-clamp-2 break-words">
                      <span className="sm:hidden">{shortName}</span>
                      <span className="hidden sm:inline">{name}</span>
                    </h3>
                    <p className="hidden sm:block text-xs opacity-70 mt-auto pt-1 line-clamp-2">
                      {description}
                    </p>
                  </button>
                );
              })}
          </div>
        </div>
      </div>

      {/* Fast statusbar i bunnen - kompakt på mobil */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
        <div className="bg-gray-100 px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 max-w-4xl mx-auto">
            <div className="flex items-center space-x-2 sm:space-x-4 overflow-hidden">
              <span className="whitespace-nowrap">{t('dashboard.system')}: <span className="text-green-600 font-medium">{t('dashboard.online')}</span></span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline whitespace-nowrap">{t('dashboard.shift')}: {t('dashboard.dayShift')}</span>
            </div>
            <div className="text-[10px] sm:text-xs whitespace-nowrap">
              {t('dashboard.published', {}, 'Publisert')}: {publishedDate || t('dashboard.loading', {}, 'Laster...')}
              {appVersion && <span className="ml-2 text-gray-400">v{appVersion}</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
